/**
 * Error sanitization utilities for user-facing error messages.
 *
 * Converts technical error messages into user-friendly text while
 * preserving the original error for logging/debugging.
 */

/**
 * Sanitizes an error for user display.
 *
 * Maps technical error messages to user-friendly text.
 * Always logs the original error for debugging.
 *
 * @param error - The error to sanitize (Error object, string, or unknown)
 * @returns User-friendly error message
 */
export function sanitizeError(error: unknown): string {
  // Log the real error for debugging
  console.error('[Error]', error);

  // Extract error message
  let errorMessage = '';
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    errorMessage = 'Unknown error';
  }

  // Convert to lowercase for case-insensitive matching
  const lowerMessage = errorMessage.toLowerCase();

  // Network errors
  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('connection') ||
    lowerMessage.includes('err_connection')
  ) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out') || lowerMessage.includes('deadline')) {
    return 'Request timed out. Please try again.';
  }

  // Authentication errors
  if (
    lowerMessage.includes('401') ||
    lowerMessage.includes('403') ||
    lowerMessage.includes('unauthorized') ||
    lowerMessage.includes('forbidden')
  ) {
    return 'Authentication error. Please refresh the page and log in again.';
  }

  // Server errors (500, 502, 503)
  if (
    lowerMessage.includes('500') ||
    lowerMessage.includes('502') ||
    lowerMessage.includes('503') ||
    lowerMessage.includes('internal server') ||
    lowerMessage.includes('bad gateway') ||
    lowerMessage.includes('service unavailable')
  ) {
    return 'Service temporarily unavailable. Please try again in a moment.';
  }

  // Bad request errors
  if (lowerMessage.includes('400') || lowerMessage.includes('bad request') || lowerMessage.includes('invalid')) {
    return 'Invalid request. Please try rephrasing your message.';
  }

  // Rate limiting
  if (
    lowerMessage.includes('429') ||
    lowerMessage.includes('too many requests') ||
    lowerMessage.includes('rate limit')
  ) {
    return 'Too many requests. Please wait a moment and try again.';
  }

  // Stream-specific errors
  if (lowerMessage.includes('stream')) {
    return 'Connection interrupted. Please try sending your message again.';
  }

  // Default fallback
  return 'An unexpected error occurred. Please try again.';
}
